import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  Divider,
  DialogActions,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Dot from 'components/common/dot/Dot';
import UserAvatar from 'components/common/userAvatar/UserAvatar';
import CustomDialogTitle from 'components/modal/CustomDialogTitle';
import HandleOperation from 'components/utils/HandleOperation';
import useCompanyQueryData from 'hooks/useCompanyQueryData';
import { UserType } from 'hooks/useUserData';
import { Dispatch, Fragment, SetStateAction, useCallback, useRef } from 'react';
import { Virtuoso } from 'react-virtuoso';

type NotifiedUsersModalProps = {
  open: boolean;
  users: UserType[];
  setOpen: Dispatch<SetStateAction<boolean>>;
};

function NotifiedUsersModal({ open, users, setOpen }: NotifiedUsersModalProps) {
  const contentRef = useRef<HTMLDivElement | undefined>(undefined);
  const { shouldDisplayCompanyOnUserCards } = useCompanyQueryData();

  const handleCloseModal = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth="sm"
      fullWidth
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
      sx={{ '& .MuiPaper-root': { minHeight: '50%' } }}
    >
      <CustomDialogTitle onClose={handleCloseModal} id="dialog-title">
        <Title>Members to Notify</Title>
      </CustomDialogTitle>

      <DialogContent id="dialog-description" dividers sx={{ p: 0 }} ref={contentRef}>
        <HandleOperation data={users}>
          <Virtuoso
            useWindowScroll
            customScrollParent={contentRef.current}
            data={users}
            itemContent={(index, item) => (
              <Fragment key={item.id}>
                <UserHoverContainer title={item.username}>
                  <UserContainer>
                    <UserAvatar
                      title={item.userNameToDisplay?.trim() || item.username}
                      avatarName={item.userNameToDisplay?.trim() || item.username}
                      subTitle={
                        <>
                          {item.title}
                          {shouldDisplayCompanyOnUserCards &&
                            (item?.primaryCompanyName || item?.userPrimaryCompanyName) && (
                              <>
                                <Dot />
                                {item?.primaryCompanyName || item?.userPrimaryCompanyName}
                              </>
                            )}
                        </>
                      }
                      avatarSize={40}
                      userName={item.username}
                      target="_blank"
                      profileImageURL={item.profileImageURL}
                    />
                  </UserContainer>
                  {users.length - 1 !== index && <Divider />}
                </UserHoverContainer>
              </Fragment>
            )}
          />
        </HandleOperation>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCloseModal} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const Title = styled(Typography, { label: 'Title' })(({ theme }) => ({
  fontSize: 24,
  fontWeight: 700,
  color: theme.palette.custom.black,
}));

const UserHoverContainer = styled(Box, { label: 'UserHoverContainer' })(({ theme }) => ({
  padding: '0 24px',
  '&:hover': {
    backgroundColor: theme.palette.custom.whiteSmoke,
    transition: '0.5s',
  },
}));

const UserContainer = styled(Box, { label: 'UserContainer' })(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 0',
}));

export default NotifiedUsersModal;
